import { consola } from 'consola'
import { escapeHtml } from '#shared/utils/html-escape'
import { RANGE_DASH, resolveParagraphRange, withParagraphAnchor } from './journals/paragraph-reference'
import { resolvePageRange } from './journals/page-reference'
import { transformShortTitle } from './solr/law-utils'

/**
 * Checks if a paragraph title is valid (not an AKN path or other invalid format).
 * AKN paths like "/akn/no/act/lov/1915-08-13/5" should not be included in citations.
 */
function isValidParagraphTitle(title: string | undefined): title is string {
  if (!title) {
    return false
  }
  // AKN paths start with /akn or contain /act/lov/
  if (title.startsWith('/akn') || title.includes('/act/lov/')) {
    return false
  }
  return true
}

const ALLOWED_COPY_TAGS = new Set([
  // Block
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'br',
  'hr',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'blockquote',
  'pre',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  // Inline
  'a',
  'strong',
  'em',
  'b',
  'i',
  'sup',
  'sub',
  'code',
])

// Drop entirely (including children/text) so script source, accessible names,
// or stylesheet text never leaks into the paste destination as plain text.
const STRIPPED_COPY_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'template',
  'noscript',
])

const VOID_ALLOWED_TAGS = new Set(['br', 'hr'])

const SAFE_HREF_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:'])

function unwrapElement(el: Element): void {
  const parent = el.parentNode
  if (!parent) {
    return
  }
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el)
  }
  parent.removeChild(el)
}

function hasAncestorTag(el: Element, tag: string): boolean {
  let cursor = el.parentElement
  while (cursor) {
    if (cursor.tagName.toLowerCase() === tag) {
      return true
    }
    cursor = cursor.parentElement
  }
  return false
}

export function sanitizeCopyFragment(fragment: DocumentFragment, baseUrl: string, currentUrl: string): void {
  // Depth-first over a pre-captured child list so removals/unwraps don't disturb iteration.
  function visit(node: Node): void {
    const children = Array.from(node.childNodes)
    for (const child of children) {
      visit(child)
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return
    }
    const el = node as Element
    const tag = el.tagName.toLowerCase()

    if (STRIPPED_COPY_TAGS.has(tag)) {
      el.remove()
      return
    }

    // `<details>` / `<summary>` deliberately fall into the unwrap branch:
    // the user can only select visible text (collapsed content is `display: none`
    // and outside `Selection.toString()`), so unwrap never leaks hidden text.
    if (!ALLOWED_COPY_TAGS.has(tag)) {
      unwrapElement(el)
      return
    }

    if (tag === 'a') {
      // Nested anchors are invalid HTML and confuse Markdown editors; unwrap the inner one.
      if (hasAncestorTag(el, 'a')) {
        unwrapElement(el)
        return
      }
      const rewritten = rewriteHref(el.getAttribute('href'), baseUrl, currentUrl)
      for (const attr of Array.from(el.attributes)) {
        el.removeAttribute(attr.name)
      }
      if (rewritten === null) {
        unwrapElement(el)
        return
      }
      el.setAttribute('href', rewritten)
      return
    }

    for (const attr of Array.from(el.attributes)) {
      el.removeAttribute(attr.name)
    }

    // Drop whitespace-only allowed elements so the paste doesn't show stray blank blocks.
    // Skip legitimately-empty void elements (e.g. <br>).
    if (!VOID_ALLOWED_TAGS.has(tag) && !el.textContent?.trim()) {
      el.remove()
    }
  }
  visit(fragment)
}

export function rewriteHref(href: string | null, baseUrl: string, currentUrl: string): string | null {
  if (href === null || href === undefined) {
    return null
  }
  const trimmed = href.trim()
  if (!trimmed) {
    return null
  }
  if (trimmed.startsWith('#')) {
    return `${currentUrl}${trimmed}`
  }
  // Protocol-relative URLs (`//evil.com/foo`) bypass the scheme allowlist
  // because they inherit the page's protocol — reject before the path branch.
  if (trimmed.startsWith('//')) {
    return null
  }
  if (trimmed.startsWith('/')) {
    return `${baseUrl}${trimmed}`
  }
  try {
    const url = new URL(trimmed)
    return SAFE_HREF_SCHEMES.has(url.protocol) ? trimmed : null
  }
  catch {
    return null
  }
}

/**
 * Returns sanitized HTML for a Range, or an empty string if extraction fails.
 *
 * The caller is responsible for adding the citation and handling fallback content.
 *
 * Run this when the text is selected, not when copy is clicked, since the DOM
 * may change between selection and copy.
 */
export function serializeSelection(range: Range, baseUrl: string, currentUrl: string): string {
  try {
    const fragment = range.cloneContents()
    sanitizeCopyFragment(fragment, baseUrl, currentUrl)
    const container = document.createElement('div')
    container.appendChild(fragment)
    return container.innerHTML.trim()
  }
  catch {
    return ''
  }
}
/**
 * Context information needed to generate a proper citation
 * Contains document metadata that cannot be derived from the URL
 */
export interface CitationContext {
  /** The type of source being cited */
  source: 'law' | 'literature' | 'article' | 'commentary'
  /** Document title for the citation */
  title?: string
  /** URL override (uses current URL if not provided) */
  url?: string
  /** Authors of the document */
  authors?: string[]

  // Literature-specific fields
  /** Publisher (for literature) - e.g., "Universitetsforlaget" */
  publisher?: string
  /** Publication year (for literature) - e.g., 2025 */
  year?: string | number
  /** Page number (for literature) - e.g., "5" or 5 */
  page?: string | number

  // Law-specific fields
  /** Law ID (for laws) - e.g., "19. juni 1992 nr. 56" */
  lawId?: string
  /** Paragraph/section title (for laws) - e.g., "§ 1" */
  paragraphTitle?: string

  // Version metadata — records which version of the content was cited/marked
  /** Solr timestamp of the node/commentary the selection was made in */
  timestamp?: string
  /** Solr timestamp of the law document */
  lawTimestamp?: string
  dateOfPublication?: string
  dateInForce?: string
  lastChangeInForce?: string

  // Commentary-specific fields
  /** Short title (for commentaries) - alternative to full title */
  shortTitle?: string
  /** Last updated date (for commentaries) - e.g., "2023-06-16" */
  lastUpdated?: string
  /** Law fragment the commentary belongs to - e.g., "paragraf-1" */
  lawFragmentId?: string
  chapterTitle?: string
  editionId?: string
  originalPublicationDate?: string
  legalAreas?: string[]
  authorSlugs?: string[]

  // Literature-specific fields
  isbn?: string[]
  editionNumber?: string

  // Journal-specific fields
  volume?: string
  issue?: string
  journalTitle?: string
  firstPage?: string | number
  lastPage?: string | number
  /** Print page the selected section starts on, from `data-citation-start-page` (journal articles with page data) */
  startPage?: string | number
  /** Article-wide paragraph number of the selection, "12" or "12–14" */
  paragraph?: string

  /** Additional metadata fields */
  [key: string]: string | number | string[] | undefined
}

/**
 * Represents a citation in both HTML and plain text formats
 */
export interface Citation {
  /** Citation formatted as HTML with links */
  htmlText: string
  /** Citation formatted as plain text */
  plainText: string
}

/**
 * Pre-computed clipboard payloads, sealed at selection-time so the copy
 * survives any DOM mutation between selection and click.
 */
export interface SelectedTextData {
  /** `text/plain` payload — selected text followed by the plain-text citation. */
  plainText: string
  /** `text/html` payload — sanitized body followed by the HTML citation. */
  htmlPayload: string
}

function formatJournalReference(context: CitationContext): string {
  const head = [context.journalTitle, context.year].filter(Boolean).join(' ')
  const span = context.firstPage && context.lastPage ? `s. ${context.firstPage}${RANGE_DASH}${context.lastPage}` : ''
  const locator = context.page
    ? `(s. ${context.page})`
    : context.paragraph
      ? `(avsnitt ${context.paragraph})`
      : ''
  return [head, span, locator].filter(Boolean).join(' ')
}

/**
 * Generates a formatted citation based on the provided context
 * This is a pure function with no side effects
 *
 * @param context - The context information for generating the citation
 * @param url - URL to use (defaults to current window location)
 * @returns Citation object with both HTML and plain text formats
 *
 * @example Literature book citation (single page)
 * ```typescript
 * const citation = generateCitation({
 *   source: 'literature',
 *   authors: ['Viggo Hagstrøm', 'Herman Bruserud'],
 *   title: 'Kjøpsrett',
 *   page: '5'
 * })
 * // Returns: "Viggo Hagstrøm og Herman Bruserud, Kjøpsrett, Universitetsforlaget, 2025 s. 5, Juridika (kopiert 01. juli 2025)"
 * // Format: {{book.authors}}, {{book.title}}, s. {{book_page.pageNumberString}}, Juridika (kopiert {{currentDate}})
 * // URL: https://juridika.no/fagbok/kj%C3%B8psrett/3/dokument#forord-til-1-utgave
 * ```
 *
 * @example Literature book citation (page range)
 * ```typescript
 * const citation = generateCitation({
 *   source: 'literature',
 *   authors: ['Viggo Hagstrøm', 'Herman Bruserud'],
 *   title: 'Kjøpsrett',
 *   page: '5-7'  // Detected when selection spans multiple pages
 * })
 * // Returns: "Viggo Hagstrøm og Herman Bruserud, Kjøpsrett, Universitetsforlaget, 2025 s. 5-7, Juridika (kopiert 01. juli 2025)"
 * ```
 *
 * @example Law citation
 * ```typescript
 * const citation = generateCitation({
 *   source: 'law',
 *   title: 'Lov 19. juni 1992 nr. 56 om handelsagenter og handelsreisende § 1'
 * })
 * // Returns: "Lov 19. juni 1992 nr. 56 om handelsagenter og handelsreisende § 1, Juridika (kopiert 01. juli 2025)"
 * // Format: Lov {{law.id}}, {{law.title}}, {{subsection.paragraphTitle}}, Juridika (kopiert {{currentDate}})
 * // URL: https://juridika.no/no/lov/1992-06-19-56/%C2%A71
 * ```
 *
 * @example Law commentary citation
 * ```typescript
 * const citation = generateCitation({
 *   source: 'commentary',
 *   authors: ['Alice Jervell', 'Julie Frydenlund O\'Leary', 'Lars Grøndal'],
 *   title: 'Lov 23. juni 2020 nr. 98 om forbrukerklagenemnda',
 *   shortTitle: 'Forbrukerklageloven',
 *   paragraphTitle: '§ 1. Virkeområde',
 *   lastUpdated: '2023-06-16'
 * })
 * // Returns: "Alice Jervell, Julie Frydenlund O'Leary, Lars Grøndal, Forbrukerklageloven: Kommentarutgave, § 1. Virkeområde, Juridika, sist ajourført 16. juni 2023"
 * // Format: {{authors}}, {{law.shortTitle}}: Kommentarutgave, {{paragraphTitle}}, Juridika, sist ajourført {{lastUpdatedDate}}
 * // URL: https://juridika.no/lov/2020-06-23-98/%C2%A71/kommentar/
 * ```
 */

export function generateCitation(context: CitationContext, url?: string): Citation {
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '')
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''

  // Format date as "01. juli 2025"
  const now = new Date()
  const currentDate = now.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  let citationText = ''
  let htmlCitationText = ''

  if (context.source === 'literature') {
    // Literature format: {{book.authors}}, {{book.title}}, {{publisher}}, {{year}} s. {{page}}, Juridika (kopiert {{currentDate}})
    const parts: string[] = []
    const htmlParts: string[] = []

    // Add authors (joined with 'og' for Norwegian)
    if (context.authors && context.authors.length > 0) {
      const authors = context.authors.join(' og ')
      parts.push(authors)
      htmlParts.push(escapeHtml(authors))
    }

    // Add title (no quotes for literature)
    if (context.title) {
      parts.push(context.title)
      htmlParts.push(`<a href="${escapeHtml(currentUrl)}">${escapeHtml(context.title)}</a>`)
    }

    // Add publisher
    if (context.publisher) {
      parts.push(context.publisher)
      htmlParts.push(escapeHtml(context.publisher))
    }

    // Add year and page
    if (context.year) {
      let yearPart = String(context.year)
      if (context.page) {
        yearPart += ` s. ${context.page}`
      }
      parts.push(yearPart)
      htmlParts.push(escapeHtml(yearPart))
    }

    // Join all parts with commas
    citationText = parts.join(', ')
    htmlCitationText = htmlParts.join(', ')

    // Add source and date
    if (citationText) {
      citationText += ', '
      htmlCitationText += ', '
    }
    citationText += `Juridika (kopiert ${currentDate})`
    htmlCitationText += `<a href="${escapeHtml(baseUrl)}">Juridika</a> (kopiert ${currentDate})`
  }
  else if (context.source === 'law') {
    // Law format: Lov {{law.id}}, {{law.shortTitle}}, {{subsection.paragraphTitle}}, Juridika (kopiert {{currentDate}})
    const parts: string[] = []
    const htmlParts: string[] = []

    // Add the law ID with a "Lov" prefix. Some upstream callers already provide `context.lawId` prefixed with `lov-`
    // (for example from URL slugs). Strip the prefix to avoid rendering values like `Lov lov-1997-06-13-44`.
    if (context.lawId) {
      const normalizedLawId = context.lawId.replace(/^lov-/i, '')
      const lawId = `Lov ${normalizedLawId}`
      parts.push(lawId)
      htmlParts.push(escapeHtml(lawId))
    }

    // Add law short title (preferred) or fall back to full title
    // Transform the short title to get the clean version (e.g., "Agenturloven" from "§ 1 Agenturloven")
    if (context.shortTitle) {
      const lawTitle = transformShortTitle(context.shortTitle).title
      if (lawTitle) {
        parts.push(lawTitle)
        htmlParts.push(`<a href="${escapeHtml(currentUrl)}">${escapeHtml(lawTitle)}</a>`)
      }
    }
    else if (context.title) {
      parts.push(context.title)
      htmlParts.push(`<a href="${escapeHtml(currentUrl)}">${escapeHtml(context.title)}</a>`)
    }

    // Add paragraph title
    if (context.paragraphTitle) {
      parts.push(context.paragraphTitle)
      htmlParts.push(escapeHtml(context.paragraphTitle))
    }

    // Join all parts with commas
    citationText = parts.join(', ')
    htmlCitationText = htmlParts.join(', ')

    // Add source and date
    if (citationText) {
      citationText += ', '
      htmlCitationText += ', '
    }
    citationText += `Juridika (kopiert ${currentDate})`
    htmlCitationText += `<a href="${escapeHtml(baseUrl)}">Juridika</a> (kopiert ${currentDate})`
  }
  else if (context.source === 'commentary') {
    // Commentary format: {{authors}}, *{{law.shortTitle}}: Kommentarutgave*, {{paragraphTitle}}, Juridika, sist ajourført {{lastUpdatedDate}}
    const parts: string[] = []
    const htmlParts: string[] = []

    // Add authors
    if (context.authors && context.authors.length > 0) {
      const authors = context.authors.join(', ')
      parts.push(authors)
      htmlParts.push(escapeHtml(authors))
    }

    // Add law short title with ": Kommentarutgave" in italic
    // Transform the short title to get the clean version (e.g., "Agenturloven" from "§ 1 Agenturloven")
    if (context.shortTitle) {
      const lawTitle = transformShortTitle(context.shortTitle).title
      if (lawTitle) {
        const titleWithType = `${lawTitle}: Kommentarutgave`
        parts.push(titleWithType)
        htmlParts.push(`<em>${escapeHtml(titleWithType)}</em>`)
      }
    }
    else if (context.title) {
      const titleWithType = `${context.title}: Kommentarutgave`
      parts.push(titleWithType)
      htmlParts.push(`<em>${escapeHtml(titleWithType)}</em>`)
    }

    // Add paragraph reference (from comment.title)
    // Use "Forord" if the title is invalid (e.g., AKN paths from foreword sections)
    if (isValidParagraphTitle(context.paragraphTitle)) {
      parts.push(context.paragraphTitle)
      htmlParts.push(`<a href="${escapeHtml(currentUrl)}">${escapeHtml(context.paragraphTitle)}</a>`)
    }
    else {
      parts.push('Forord')
      htmlParts.push(`<a href="${escapeHtml(currentUrl)}">Forord</a>`)
    }

    // Join all parts with commas
    citationText = parts.join(', ')
    htmlCitationText = htmlParts.join(', ')

    // Add source and last updated date
    // Use lastUpdated if available, otherwise fall back to current date
    const lastUpdatedDate = context.lastUpdated
      ? new Date(context.lastUpdated).toLocaleDateString('nb-NO', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })
      : currentDate

    if (citationText) {
      citationText += ', '
      htmlCitationText += ', '
    }
    citationText += `Juridika, sist ajourført ${lastUpdatedDate}`
    htmlCitationText += `<a href="${escapeHtml(baseUrl)}">Juridika</a>, sist ajourført ${lastUpdatedDate}`
  }
  else {
    // Article format: {{authors}}, «{{title}}», {{journalTitle}} {{year}} s. {{firstPage}}–{{lastPage}} ({{locator}}), Juridika (kopiert {{currentDate}})
    const parts: string[] = []
    const htmlParts: string[] = []

    if (context.authors && context.authors.length > 0) {
      const authors = context.authors.join(', ')
      parts.push(authors)
      htmlParts.push(escapeHtml(authors))
    }

    if (context.title) {
      const articleUrl = withParagraphAnchor(currentUrl, context.paragraph)
      parts.push(`«${context.title}»`)
      htmlParts.push(`«<a href="${escapeHtml(articleUrl)}">${escapeHtml(context.title)}</a>»`)
    }

    const reference = formatJournalReference(context)
    if (reference) {
      parts.push(reference)
      htmlParts.push(escapeHtml(reference))
    }

    citationText = parts.join(', ')
    htmlCitationText = htmlParts.join(', ')

    if (citationText) {
      citationText += ', '
      htmlCitationText += ', '
    }
    citationText += `Juridika (kopiert ${currentDate})`
    htmlCitationText += `<a href="${escapeHtml(baseUrl)}">Juridika</a> (kopiert ${currentDate})`
  }

  // Generate both plain text and HTML versions
  const plainTextCitation = `\n\n${citationText}`
  const htmlCitation = `<p>${htmlCitationText}</p>`

  return {
    htmlText: htmlCitation,
    plainText: plainTextCitation,
  }
}

export function buildCopyPayload(range: Range, citationContext: CitationContext, fallbackText: string): SelectedTextData {
  const citation = generateCitation(citationContext)
  const text = range.toString().trim() || fallbackText
  const htmlBody = serializeSelection(range, window.location.origin, window.location.href)

  // Fallback returns a single plain-text `<p>`, so formatting like bold, italics, and links is lost.
  // Today this only happens for selections that become empty after sanitizing.
  if (!htmlBody) {
    consola.warn('[copy-with-source] empty body fallback', { source: citationContext.source })
  }

  return {
    plainText: text + citation.plainText,
    htmlPayload: htmlBody ? `${htmlBody}${citation.htmlText}` : `<p>${escapeHtml(text)}</p>${citation.htmlText}`,
  }
}

/**
 * Validates that a text selection is valid and non-empty
 *
 * @param text - The selected text to validate
 * @param ranges - The selection ranges from the browser
 * @returns True if selection is valid, false otherwise
 */
export function isValidTextSelection(text: string | null, ranges: Range[] | null): boolean {
  const trimmedText = text?.trim()
  return !!(trimmedText && ranges && ranges.length > 0)
}

/**
 * Detects citation context from HTML attributes in the selected text's DOM
 * Much simpler than complex traversal - just reads pre-computed citation data
 *
 * @param range - The text selection range
 * @returns Complete citation context from attributes, undefined if not found
 *
 * @example Single element selection
 * ```typescript
 * const context = detectCitationFromSelection(range)
 * // Returns: { source: 'literature', title: 'Kjøpsrett', page: '5', ... }
 * ```
 *
 * @example Multi-element selection (page range)
 * ```typescript
 * const context = detectCitationFromSelection(range)
 * // Returns: { source: 'literature', title: 'Kjøpsrett', page: '5-7', ... }
 * ```
 */
export function detectCitationFromSelection(range: Range): CitationContext | undefined {
  const startElement = findElementWithCitationAttributes(range.startContainer)
  const endElement = findElementWithCitationAttributes(range.endContainer)

  if (!startElement) {
    return undefined
  }

  const startContext = extractCitationFromElement(startElement)
  if (!startContext) {
    return undefined
  }

  const endContext = endElement && endElement !== startElement ? extractCitationFromElement(endElement) : null
  const context = endContext ? combineContextsForRange(startContext, endContext) : startContext

  if (context.source === 'article') {
    const paragraph = resolveParagraphRange(range)
    if (paragraph) {
      context.paragraph = paragraph
    }
    const page = resolvePageRange(range)
    if (page) {
      context.page = page
    }
  }

  return context
}

/**
 * Finds the nearest parent element that has citation attributes
 * @param node - The DOM node to start searching from
 * @returns Element with citation attributes or null
 */
function findElementWithCitationAttributes(node: Node): Element | null {
  let element = node

  // If it's a text node, get its parent element
  if (element.nodeType === Node.TEXT_NODE) {
    element = element.parentElement!
  }

  // Traverse up the DOM to find an element with citation source
  while (element && element !== document.body) {
    if (element instanceof Element && element.getAttribute('data-citation-source')) {
      return element
    }
    element = element.parentElement!
  }

  return null
}

const CITATION_ATTR_PREFIX = 'data-citation-'

// Comma-separated attributes stored back as arrays.
const CITATION_ARRAY_FIELDS = new Set(['authors', 'authorSlugs', 'legalAreas', 'isbn'])

function isCitationSource(value: string): value is CitationContext['source'] {
  return value === 'law' || value === 'literature' || value === 'article' || value === 'commentary'
}

function citationKeyFromAttr(name: string): string {
  return name.slice(CITATION_ATTR_PREFIX.length).replace(/-([a-z])/g, (_, char: string) => char.toUpperCase())
}

/**
 * Extracts citation context from element attributes. Collects EVERY
 * `data-citation-*` attribute (kebab-case → camelCase), so new attributes on
 * the reading surfaces flow into copies and stored markings automatically.
 */
function extractCitationFromElement(element: Element): CitationContext | null {
  const source = element.getAttribute('data-citation-source')
  if (!source || !isCitationSource(source)) {
    return null
  }

  const citationContext: CitationContext = { source }
  for (const attr of Array.from(element.attributes)) {
    if (!attr.name.startsWith(CITATION_ATTR_PREFIX) || attr.name === 'data-citation-source') {
      continue
    }
    const value = attr.value.trim()
    if (!value) {
      continue
    }
    const key = citationKeyFromAttr(attr.name)
    citationContext[key] = CITATION_ARRAY_FIELDS.has(key)
      ? value.split(',').map(part => part.trim()).filter(Boolean)
      : value
  }

  if (!citationContext.shortTitle && typeof citationContext.commentaryTitle === 'string') {
    citationContext.shortTitle = citationContext.commentaryTitle
  }

  return citationContext
}

/**
 * Combines citation contexts for range selections (e.g., page 5-7, § 1-3)
 * @param start - Citation context from start element
 * @param end - Citation context from end element
 * @returns Combined citation context with ranges
 */
function combineContextsForRange(start: CitationContext, end: CitationContext): CitationContext {
  const combined = { ...start }

  if (start.source === 'literature' && start.page && end.page) {
    // Literature page ranges: "5" + "7" = "5-7"
    combined.page = start.page === end.page ? start.page : `${start.page}-${end.page}`
  }
  else if (start.source === 'law' && start.paragraphTitle && end.paragraphTitle) {
    // Law section ranges: "§ 1" + "§ 3" = "§ 1-3"
    if (start.paragraphTitle === end.paragraphTitle) {
      combined.paragraphTitle = start.paragraphTitle
    }
    else {
      const startSection = start.paragraphTitle.replace('§ ', '')
      const endSection = end.paragraphTitle.replace('§ ', '')
      combined.paragraphTitle = `§ ${startSection} til § ${endSection}`
    }
  }

  return combined
}

/**
 * Clears all text selection ranges from the browser
 * This is a utility wrapper around browser selection API
 */
export function clearBrowserTextSelection(): void {
  if (typeof window !== 'undefined' && window.getSelection) {
    window.getSelection()?.removeAllRanges()
  }
}
